// libraries
import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  selectEmailSendingFromState,
  selectEmailSentFromState,
  selectErrorDetailMapFromState,
  selectTopLevelErrorFromState,
} from 'src/app/ResetPassword/redux/resetPassword.selectors'
import Auth0ForgotPassword from 'src/components/Auth0ForgotPassword/Auth0ForgotPassword'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ForgotPasswordForm from 'src/components/ForgotPasswordForm/ForgotPasswordForm'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import SvgCodeVerification from 'src/components/Svgs/SvgCodeVerification'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import TopLevelError from 'src/components/TopLevelError/TopLevelError'
import { AuthTypes } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { signinUrl } from 'src/navigation/urls'
import { createSetEmail, postAuthType } from 'src/redux/actions/auth.actions'
import {
  selectAuthTypeFromState,
  selectEmailFromState,
  selectFetchingAuthTypeFromState,
  selectNullSafeLocaleFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

interface ResetPasswordFormProps {
  title?: React.ReactNode
  confirmPassword?: boolean
  setTopRightImage?: (image: React.ReactElement) => void
}

const ResetPasswordForm: React.FC<ResetPasswordFormProps> = (props) => {
  const dispatch = useBackgroundDispatch()
  const translate = useTranslate()

  const email = useSelector(selectEmailFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const emailSent = useSelector(selectEmailSentFromState)
  const emailSending = useSelector(selectEmailSendingFromState)
  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const appName = useSelector(selectAppName)
  const authType = useSelector(selectAuthTypeFromState)
  const isFetchingAuthType = useSelector(selectFetchingAuthTypeFromState)
  const isSending = emailSending || isFetchingAuthType

  const onEmailChange = (val: string): void => {
    dispatch(createSetEmail(val))
  }

  const [showUpdatePassword, setShowUpdatePassword] = useState(false)

  const mustResetPassword = window.location.search.includes('mustResetPassword')

  const formTitle = mustResetPassword
    ? translate('Reset Password')
    : props.title || translate('Forgot Password')

  useEffect((): void => {
    if (emailSent && authType) {
      setShowUpdatePassword(true)
    }
  }, [authType, emailSent])

  return (
    <>
      {!showUpdatePassword ? (
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                32,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              margin-bottom: 40px;
            `}
          >
            {formTitle}
          </span>
          {mustResetPassword && (
            <AlertPanel
              variant="default"
              css={css`
                margin-bottom: 8px;
              `}
            >
              <p>
                {translate(
                  "You must reset your password to log in with this email address. Please click 'continue' to reset your password",
                )}
              </p>
            </AlertPanel>
          )}
          <p
            css={css`
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.regular,
              )};
              margin-bottom: 30px;
            `}
          >
            {translate(
              'Make sure the email address below is what you used to create a profile with ${appName}.',
              { appName },
            )}
          </p>
          <TextInputField
            design={TextInputFieldDesigns.New}
            value={email}
            placeholder={translateSingleString(
              locale,
              'Enter email connected to ${appName}',
              { appName: translateSingleString(locale, appName) },
            )}
            onChange={onEmailChange}
            type={'email'}
            autoComplete={'email'}
            errors={errorDetailMap?.data}
            fieldLabel={translate('Email')}
          />
          {topLevelError !== undefined && (
            <TopLevelError error={topLevelError} />
          )}
          <Button
            type={ButtonTypes.submit}
            css={css`
              margin-top: 40px;
            `}
            design={ButtonDesigns.designCta}
            text={translate('Continue')}
            loadingText={translate('Sending')}
            onClick={(): void => {
              props.setTopRightImage &&
                props.setTopRightImage(<SvgCodeVerification />)
              dispatch(
                postAuthType(
                  {
                    data: email,
                  },
                  !emailSent,
                ),
              )
            }}
            loading={isSending}
            disabled={isSending}
            big
          />
          <HoverableLink
            to={signinUrl()}
            css={css`
              margin-top: 30px;
              align-self: center;
            `}
            underline
            ariaLabelSource={'Back to Login'}
          >
            {translate('Back to Login')}
          </HoverableLink>
        </div>
      ) : authType === AuthTypes.cognito ? (
        <ForgotPasswordForm email={email} />
      ) : (
        <Auth0ForgotPassword />
      )}
    </>
  )
}

export default ResetPasswordForm
